import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Stack,
  IconButton,
  Menu,
  MenuItem,
  TextField,
  Checkbox,
  Divider,
  Paper,
  InputAdornment
} from '@mui/material';
import { KeyboardArrowDown as KeyboardArrowDownIcon } from '@mui/icons-material';
import { v4 as uuidv4 } from 'uuid';

export interface LabelFilterPair {
  id: string;
  key: string;
  values: string[];
}

interface LabelFilterMenuProps {
  open: boolean;
  anchorEl: HTMLElement | null;
  onClose: () => void;
  onApply: (filters: LabelFilterPair[]) => void;
  labelKeys: (string | { [key: string]: any })[];
  labelValues: { [key: string]: (string | { [key: string]: any })[] };
  onFetchLabelValues: (key: string) => void;
  appliedFilters: LabelFilterPair[];
  loadingLabelKey: string | null; // Tracks which label key is currently loading
}

const LabelFilterMenu: React.FC<LabelFilterMenuProps> = ({
  open,
  anchorEl,
  onClose,
  onApply,
  labelKeys,
  labelValues,
  onFetchLabelValues,
  appliedFilters,
  loadingLabelKey,
}) => {
  const [initialEmptyId] = useState(() => uuidv4());
  const [tempFilters, setTempFilters] = useState<LabelFilterPair[]>([]);

useEffect(() => {
    if (open) {
      const sourceFilters = appliedFilters.length > 0
        ? appliedFilters
        : [{ id: initialEmptyId, key: '', values: [] }];
      setTempFilters(sourceFilters);

      // When the menu opens, ensure the values for any applied filters are loaded.
      sourceFilters.forEach(filter => {
        if (filter.key) {
          onFetchLabelValues(filter.key);
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, appliedFilters, initialEmptyId]);

  const handleAddMore = () => {
    setTempFilters([...tempFilters, { id: uuidv4(), key: '', values: [] }]);
  };

  const handleDelete = (id: string) => {
    setTempFilters(tempFilters.filter(f => f.id !== id));
  };

  const handleUpdateFilter = (id: string, newFilter: Partial<LabelFilterPair>) => {
    setTempFilters(tempFilters.map(f => (f.id === id ? { ...f, ...newFilter } : f)));
  };

  const handleApplyClick = () => {
    const validFilters = tempFilters.filter(f => f.key && f.values.length > 0);
    onApply(validFilters);
    onClose();
  };

  const handleClearClick = () => {
    const newFilters = [{ id: initialEmptyId, key: '', values: [] }];
    setTempFilters(newFilters);
    onApply(newFilters.filter(f => f.key));
  };

  return (
    <Menu
      anchorEl={anchorEl}
      open={open}
      onClose={onClose}
      MenuListProps={{ 'aria-labelledby': 'label-filter-button', disablePadding: true }}
      PaperProps={{
        elevation: 1,
        sx: {
          width: 650,
          p: 2,
          borderRadius: '12px',
          mt: 1,
        }
      }}
    >
      <Typography variant="h6" sx={{ mb: 1, fontWeight: 600, fontSize: '14px', color: ' #666666' }}>Select Label</Typography>
      <Divider sx={{ mx: -2 }} />

      <Paper elevation={0} sx={{ p: 2, borderRadius: '8px', backgroundColor: 'rgba(249, 250, 251, 1)', mt: 2, border: 'none' }}>
        <Stack spacing={2}>
          {tempFilters.map((filter) => (
            <Stack key={filter.id} direction="row" spacing={1} alignItems="flex-end">
              <SearchableDropdown
                label={<>Key<span style={{ color: 'red' }}>*</span></>}
                options={labelKeys}
                value={filter.key}
                onChange={(newKey) => {
                  handleUpdateFilter(filter.id, { key: newKey as string, values: [] });
                  if (newKey) onFetchLabelValues(newKey as string);
                }}
                searchPlaceholder="Search by Key.."
              />
              <SearchableDropdown
                label={<>Value<span style={{ color: 'red' }}>*</span></>}
                options={labelValues[filter.key] || []}
                value={filter.values.length > 1 ? `${filter.values.length} selected` : filter.values.join(', ')}
                multiple
                selectedValues={filter.values}
                onChange={(newValues) => handleUpdateFilter(filter.id, { values: newValues as string[] })}
                disabled={!filter.key}
                searchPlaceholder="Search by value.."
                isFetching={loadingLabelKey === filter.key}
              />
              <IconButton onClick={() => handleDelete(filter.id)} disabled={tempFilters.length === 1}>
                <img src="/icons/delete_icon.svg" alt="Delete" style={{ width: 16, height: 16 }} />
              </IconButton>
            </Stack>
          ))}
        </Stack>
      </Paper>

      <Button onClick={handleAddMore} sx={{ mt:0.5, textTransform: 'none', color: '#0064FF',fontSize:'12px', fontWeight: 500 }}>
        + Add More
      </Button>
      <Divider sx={{ mx: -2,mt:1,mb:1 }} />
      <Box sx={{ display: 'flex', justifyContent: 'flex-end'}}>
        <Button onClick={handleClearClick} variant="text" sx={{
            textTransform: 'none',
            color: '#666666',
            fontFamily: 'Inter',
            fontWeight: 500,
            fontSize: '14px',
            lineHeight: '16px',
            marginRight: '-10px',
            marginBottom: '-10px'
        }}>Clear</Button>
        <Button onClick={handleApplyClick} variant="text" sx={{
            textTransform: 'none',
            color: '#0064FF',
            fontFamily: 'Inter',
            fontWeight: 500,
            fontSize: '14px',
            lineHeight: '16px',
            marginRight: '-8px' ,
            marginBottom: '-10px'
        }}>Apply</Button>
      </Box>
    </Menu>
  );
};

const SearchableDropdown: React.FC<{
  label: React.ReactNode;
  options: (string | { [key: string]: any })[];
  value: string;
  onChange: (value: string | string[]) => void;
  searchPlaceholder: string;
  multiple?: boolean;
  selectedValues?: string[];
  disabled?: boolean;
  isFetching?: boolean;
}> = ({ label, options, value, onChange, multiple, selectedValues = [], disabled, searchPlaceholder, isFetching = false }) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [searchTerm, setSearchTerm] = useState('');

  const open = Boolean(anchorEl);

  const handleOpenMenu = (event: React.MouseEvent<HTMLElement>) => {
    if (disabled) return;
    setAnchorEl(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
    setSearchTerm('');
  };

  const getOptionText = (option: string | { [key: string]: any }): string => {
    if (typeof option === 'string') return option;
    if (option && typeof option === 'object') return String(option.name || option.value || '');
    return '';
  };

  const filteredOptions = options.filter(option =>
    getOptionText(option).toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleSelect = (optionValue: string) => {
    if (multiple) {
      const newSelection = selectedValues.includes(optionValue)
        ? selectedValues.filter(v => v !== optionValue)
        : [...selectedValues, optionValue];
      onChange(newSelection);
    } else {
      onChange(optionValue);
      handleCloseMenu();
    }
  };

  const handleSelectAll = () => {
    const allFilteredTexts = filteredOptions.map(getOptionText);
    const areAllSelected = allFilteredTexts.length > 0 && allFilteredTexts.every(opt => selectedValues.includes(opt));

    if (areAllSelected) {
      const newSelection = selectedValues.filter(val => !allFilteredTexts.includes(val));
      onChange(newSelection);
    } else {
        const newSelection = Array.from(new Set([...selectedValues, ...allFilteredTexts]));
      onChange(newSelection);
    }
  };

   const menuItemStyle = {
      fontSize: '14px',
      fontWeight: 400,
      color: 'rgba(102, 102, 102, 1)',
      py:1
  };

  return (
    <Stack spacing={0.5} sx={{ width: '100%' }}>
      <Typography variant="body2" sx={{ fontWeight: 400, color: '#333333', fontSize: '14px' }}>
        {label}
      </Typography>
      <TextField
        placeholder={!multiple ? 'Select Key' : 'Select value'}
        value={value}
        onClick={handleOpenMenu}
        disabled={disabled}
        fullWidth
        InputProps={{
          readOnly: true,
          endAdornment: (
            <InputAdornment position="end">
              <KeyboardArrowDownIcon sx={{ color: '#333333', transform: open ? 'rotate(180deg)' : 'rotate(0deg)',fontSize: '20px'}} />
            </InputAdornment>
          ),
          sx: { borderRadius: '6px', height: '36px', fontSize: '14px',backgroundColor: '#FFFFFF',border:' #E8EBEC' },
        }}
        sx={{
          '& .MuiOutlinedInput-root': {
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#0064FF' },
            '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#0064FF' },
          },
            '& .MuiInputBase-input::placeholder': {
            color: '#999999',
            fontFamily: 'Inter',
            fontWeight: 400,
            fontSize: '12px',
            lineHeight: '18px',
            letterSpacing: '0%',
            opacity: 1,
          },
        }}
      />
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleCloseMenu}
        MenuListProps={{ disablePadding: true }}
        PaperProps={{ sx: { width: 255, mt: 1, borderRadius: '8px', boxShadow: '0 4px 12px rgba(0,0,0,0.15)' } }}
      >
        <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid #E5E7EB' }}>
          <TextField
            autoFocus // FIX: Ensures the input is focused when the menu opens
            placeholder={searchPlaceholder}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyDown={(e) => e.stopPropagation()}
            fullWidth
            variant="outlined"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start" sx={{ ml: -0.5 }}>
                  <img src="/icons/search_icon.svg" alt="Search" style={{ width: '16px', height: '16px' }} />
                </InputAdornment>
              ),
              sx: {
                height: '32px',
                borderRadius: '10px',
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
                '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
                '& .MuiInputBase-input::placeholder': {
                  fontSize: '14px',
                  fontWeight: 300,
                }
              },
            }}
          />
        </Box>

        <Box sx={{ maxHeight: 210, overflowY: 'auto' }}>
          {multiple && filteredOptions.length > 0 && (
            <MenuItem onClick={handleSelectAll} sx={menuItemStyle}>
              <Checkbox
                  size="small"
                  checked={filteredOptions.every(opt => selectedValues.includes(getOptionText(opt)))}
                  indeterminate={
                    !filteredOptions.every(opt => selectedValues.includes(getOptionText(opt))) &&
                    filteredOptions.some(opt => selectedValues.includes(getOptionText(opt)))
                  }
                  sx={{
                    '&.Mui-checked': {
                      color: '#0064FF',
                    },
                    '&.Mui-indeterminate': {
                      color: '#0064FF',
                    },
                  }}
                />
              All
            </MenuItem>
          )}

          {/* FIX: Correctly handle fetching, no results, and data states */}
          {isFetching ? (
            <MenuItem disabled sx={{ fontSize: '12px', color: '#666' }}>
              Fetching...
            </MenuItem>
          ) : filteredOptions.length > 0 ? (
            filteredOptions.map((option, index) => {
              const optionText = getOptionText(option);
              return (
                <MenuItem
                  key={`${optionText}-${index}`}
                  onClick={() => handleSelect(optionText)}
                  selected={!multiple && value === optionText}
                  sx={{ fontSize: '14px', fontWeight: 400,color:'#666666',height: '40px',fontFamily:'Inter'}}
                >
                  {multiple && <Checkbox size="small"
                  sx={{ '&.Mui-checked': { color: '#0064FF' } }}
                  checked={selectedValues.includes(optionText)} />}
                  {optionText}
                </MenuItem>
              );
            })
          ) : (
            <MenuItem disabled>No results found</MenuItem>
          )}
        </Box>

        {multiple && (
          <Box sx={{ px: 1, py: 0.5, borderTop: '1px solid #E5E7EB', textAlign: 'right'}}>
          <Button onClick={()=> setAnchorEl(null)} size="small" sx={{ textTransform: 'none',color: '#0064FF'  }}>
                Done
             </Button>
          </Box>
        )}
      </Menu>
    </Stack>
  );
};

export default LabelFilterMenu;